<template>
    <div class="row">
        <!-- Sub Order Header Info -->
        <div v-if="isSubOrder" class="col-12 mb-0">
            <div class="alert alert-info p-1 d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                    <feather-icon icon="LayersIcon" size="16" class="mr-50" />
                    <span class="font-small-3">
                        <strong>Ana Sipariş:</strong> {{ value.MainOrderNumber || "Belirtilmemiş" }}
                        <span v-if="subOrderIndex !== null" class="ml-1">| <strong>Parça:</strong> #{{ subOrderIndex + 1 }}</span>
                    </span>
                </div>
                <div class="d-flex align-items-center gap-1">
                    <b-badge v-if="value.FuelTypeName" :variant="getFuelTypeBadgeClass(value.FuelTypeName)" class="font-small-3 mr-50">
                        {{ value.FuelTypeName }}
                    </b-badge>
                    <b-badge v-if="value.OrderQuantity" variant="light-primary" class="font-small-3">
                        {{ value.OrderQuantity.toLocaleString("tr-TR") }} lt
                    </b-badge>
                </div>
            </div>
        </div>

        <div class="col-md-6 mb-1">
            <label class="form-label">Termin Tarihi</label>
            <v-date-picker
                v-model="value.OrderDate"
                placeholder="Boş bırakılabilir"
                :disabled="isReadOnly"
                :disabled-date="disableDatesBeforeMainOrderDate"
            >
            </v-date-picker>
            <small class="text-muted d-block mt-25">Depoların stok durumuna göre planlanabilir. Ana siparişin verildiği tarihten önce seçilemez.</small>
        </div>
        <div class="col-md-6 mb-1">
            <label class="form-label">Miktar [lt] <span class="text-danger">*</span></label>
            <VCurrencyInput :value="value.OrderQuantity" @update-value="value.OrderQuantity = $event" :disabled="isReadOnly" />
        </div>

        <div class="col-md-6 mb-1">
            <label class="form-label">Birim Fiyat [TL/lt] (Ana Siparişten)</label>
            <input class="form-control" :value="value.UnitPrice ? value.UnitPrice.toFixed(2) : '0,00'" disabled />
        </div>
        <div class="col-md-6 mb-1">
            <label class="form-label">Toplam Tutar [TL]</label>
            <input class="form-control" :value="calculatedTotalAmount" disabled />
        </div>

        <div class="col-md-6 mb-1">
            <label class="form-label">Taşıma Maliyeti [TL]</label>
            <VCurrencyInput :value="value.TransportAmount" @update-value="value.TransportAmount = $event" :disabled="isReadOnly" />
        </div>
        <div class="col-md-6 mb-1">
            <label class="form-label">Şube <span class="text-danger">*</span></label>
            <v-select-list :options="branches" v-model="branch" placeholder="Şube Seçiniz" label="Name" :disabled="isReadOnly"></v-select-list>
        </div>

        <!-- Sürücü / Plaka Bilgileri (Adblue değilse) -->
        <template v-if="!isAdBlue">
            <div class="col-12 mb-75">
                <label class="form-label">Sürücü</label>
                <v-select-list
                    v-model="employee"
                    :options="employees"
                    label="FullName"
                    v-on:search="searchEmployees"
                    placeholder="Sürücü adı girin…"
                    :loading="employeeLoading"
                    :disabled="isReadOnly"
                ></v-select-list>
                <div v-if="employee && employee.IdentityNumber" class="mt-25 ml-50">
                    <small class="text-muted">
                        <feather-icon icon="UserCheckIcon" size="12" />
                        TCKN: <strong>{{ employee.IdentityNumber }}</strong>
                    </small>
                </div>
            </div>
            <div class="col-md-6 mb-1">
                <label class="form-label">Araç Plakası</label>
                <v-select-list
                    :options="truckPlates"
                    v-model="plate"
                    placeholder="Plaka Seçiniz"
                    label="PlateValue"
                    :disabled="isReadOnly"
                ></v-select-list>
            </div>
            <div class="col-md-6 mb-1">
                <label class="form-label">Treyler Plakası</label>
                <v-select-list
                    :options="trailerPlates"
                    v-model="trailerPlate"
                    placeholder="Treyler Seçiniz"
                    label="PlateValue"
                    :disabled="isReadOnly"
                ></v-select-list>
            </div>
            <div v-if="plateSelectionIsInvalid" class="col-12">
                <div class="alert alert-danger p-50 text-center" role="alert">
                    <FeatherIcon icon="AlertTriangleIcon" size="16" class="mr-50"></FeatherIcon>Araç ve treyler plakası aynı olamaz.
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import VehicleOperationsApi from "@/api/vehicle-operations";
import CompanyApi from "@/api/company";
import EmployeeOperationsApi from "@/api/employee-operations";
import FuelStorageApi from "@/api/fuel-storage";
import { BBadge } from "bootstrap-vue";
import FeatherIcon from "@/@core/components/feather-icon/FeatherIcon.vue";
import VCurrencyInput from "@/components/VCurrencyInput.vue";

export default {
    name: "FuelStorageOrderCreateOrUpdateNew",
    components: {
        VCurrencyInput,
        BBadge,
        FeatherIcon,
    },
    props: {
        value: Object,
        isReadOnly: {
            type: Boolean,
            default: false,
        },
        isSubOrder: {
            type: Boolean,
            default: false,
        },
        subOrderIndex: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            truckPlates: [],
            trailerPlates: [],
            branches: [],
            employees: [],
            employeeLoading: false,
        };
    },
    mounted() {
        this.getTruckPlates();
        this.getTrailerPlates();
        this.getBranches();
        if (this.value.DriverEmployee) {
            this.searchEmployees(this.value.DriverEmployee);
        } else if (this.value.DriverEmployeeId) {
            this.searchEmployeesById(this.value.DriverEmployeeId);
        }

        // Eğer miktar yoksa ve genel toplam belli ise tetikleme ana modalda yapılıyor.
    },
    methods: {
        getTruckPlates() {
            VehicleOperationsApi.Plate.ListAll().then((response) => {
                this.truckPlates = response.data.Data || [];
            });
        },
        getTrailerPlates() {
            VehicleOperationsApi.Plate.ListTrailer().then((response) => {
                this.trailerPlates = response.data.Data || [];
            });
        },
        getBranches() {
            // Sadece yakıt deposu olan şubeler listelenir. (Depo bulunmayan şubelere sipariş girilememesi için)
            FuelStorageApi.FuelStorage.BranchesWithFuelStorage().then((res) => {
                this.branches = res.data.Data || [];
            });
        },
        searchEmployees(search) {
            if (search) {
                this.employeeLoading = true;
                EmployeeOperationsApi.Employee.SearchActiveDriver(search)
                    .then((res) => {
                        this.employees = res.data.Data || [];
                        this.employeeLoading = false;
                    })
                    .catch(() => {
                        this.employeeLoading = false;
                    });
            }
        },
        searchEmployeesById(id) {
            this.employeeLoading = true;
            EmployeeOperationsApi.Employee.Get(id)
                .then((res) => {
                    if (res.data && res.data.Data) {
                        this.employees = [res.data.Data];
                    }
                    this.employeeLoading = false;
                })
                .catch(() => {
                    this.employeeLoading = false;
                });
        },
        getFuelTypeBadgeClass(fuelTypeName) {
            if (!fuelTypeName) return "light-secondary";
            const name = fuelTypeName.toLocaleLowerCase("tr-TR");
            if (name.includes("adblue")) return "light-primary";
            if (name.includes("motorin") || name.includes("dizel")) return "light-warning";
            return "light-secondary";
        },
        // Termin tarihi, ana siparişin verildiği tarihten önce olamaz.
        disableDatesBeforeMainOrderDate(date) {
            const mainDate = this.value && this.value._mainOrderDate;
            if (!mainDate) return false;
            const orderDate = new Date(mainDate);
            orderDate.setHours(0, 0, 0, 0);
            return date < orderDate;
        },
    },
    computed: {
        plate: {
            get: function () {
                return this.truckPlates.find((b) => b.Id == this.value.VehiclePlateId);
            },
            set: function (val) {
                this.value.VehiclePlateId = val?.Id || null;
            },
        },
        trailerPlate: {
            get: function () {
                return this.trailerPlates.find((b) => b.Id == this.value.TrailerPlateId);
            },
            set: function (val) {
                this.value.TrailerPlateId = val?.Id || null;
            },
        },
        branch: {
            get() {
                return this.branches.find((b) => b.Id === this.value.FuelOrderBranchId);
            },
            set(val) {
                this.value.FuelOrderBranchId = val?.Id || null;
            },
        },
        employee: {
            get: function () {
                return this.employees.find((s) => s.Id == this.value.DriverEmployeeId);
            },
            set: function (val) {
                this.value.DriverEmployeeId = val?.Id || null;
                this.value.DriverEmployee = val ? val.FullName : null;
            },
        },
        isAdBlue() {
            const name = (this.value?.FuelTypeName || "").toLocaleLowerCase("tr-TR");
            return name.includes("adblue");
        },
        plateSelectionIsInvalid() {
            return this.value.VehiclePlateId && this.value.TrailerPlateId && this.value.VehiclePlateId === this.value.TrailerPlateId;
        },
        calculatedTotalAmount() {
            if (this.value.OrderQuantity && this.value.UnitPrice) {
                return (this.value.OrderQuantity * this.value.UnitPrice).toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
            }
            return "0,00";
        },
        isFormInvalid() {
            return this.plateSelectionIsInvalid || !this.value.OrderQuantity || !this.value.FuelOrderBranchId;
        },
    },
    watch: {
        isFormInvalid: {
            // Modal yeniden açılıp dolu veriyle yüklendiğinde watch sadece "değişim"de
            // tetiklendiği için "Oluştur" butonunun parent'taki disabled state'i güncellenmiyordu
            // (eksik bilgileri doldursak bile parent isFormInvalid=true kalıyordu).
            // immediate: true ile mount sırasında mevcut geçerlilik durumunu yayınlıyoruz.
            handler(val) {
                this.$emit("validation-changed", val);
            },
            immediate: true,
        },
    },
};
</script>
